import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { RiCloseCircleFill } from "react-icons/ri";
import axios from "axios";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fafafa",
    "&:hover": { backgroundColor: "#f5f5f5" },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
};

const labelSx = {
  mb: 1,
  display: "block",
  fontWeight: 600,
  fontSize: { xs: "13px", sm: "14px" },
  color: "#333",
};

const gradientBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  px: { xs: 2, sm: 3 },
  py: { xs: 1, sm: 1.25 },
  background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
};

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/interesting-facts`;
const MAX_TITLE_LENGTH = 200;
const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

export default function CreateInterestingFact(props) {
  const {
    header = "Add Interesting Fact",
    button_name = "Save",
    row,
    onSave,
    onCancel,
  } = props;

  const [fact, setFact] = useState({
    title: row?.title || "",
    content: row?.content || "",
    displayOrder: row?.displayOrder ?? 0,
    isActive: row?.isActive ?? true,
    image: row?.image || null,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (row && row._id) {
      setFact({
        title: row.title || "",
        content: row.content || "",
        displayOrder: row.displayOrder ?? 0,
        isActive: row.isActive ?? true,
        image: row.image || null,
      });
    } else if (!row) {
      setFact({
        title: "",
        content: "",
        displayOrder: 0,
        isActive: true,
        image: null,
      });
    }
  }, [row]);

  const validateForm = () => {
    const newErrors = {};

    if (!fact.title?.trim()) newErrors.title = "Title is required";
    else if (fact.title.trim().length > MAX_TITLE_LENGTH) {
      newErrors.title = `Title must be at most ${MAX_TITLE_LENGTH} characters`;
    }

    if (!fact.content?.trim()) newErrors.content = "Content is required";

    const order = Number(fact.displayOrder);
    if (fact.displayOrder === "" || Number.isNaN(order) || order < 0) {
      newErrors.displayOrder = "Display order must be a number >= 0";
    }

    if (!row?._id && !(fact.image instanceof File) && !fact.image) {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert("Please upload only JPG, JPEG, PNG, or WEBP file");
      e.target.value = "";
      return;
    }

    setFact({ ...fact, image: file });
    setErrors({ ...errors, image: "" });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", fact.title.trim());
      formData.append("content", fact.content.trim());
      formData.append("displayOrder", String(Number(fact.displayOrder)));
      formData.append("isActive", String(!!fact.isActive));

      if (fact.image instanceof File) {
        formData.append("image", fact.image);
      }

      let response;
      if (row?._id) {
        response = await axios.put(`${BASE_URL}/${row._id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axios.post(BASE_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.data.success) {
        alert(response.data.message || "Interesting fact saved successfully");
        if (onSave) onSave(false);
      } else {
        alert(response.data.message || "Failed to save interesting fact");
      }
    } catch (error) {
      console.error("Error saving interesting fact:", error);
      alert(error.response?.data?.message || "Failed to save interesting fact");
    } finally {
      setSubmitting(false);
    }
  };

  const previewUrl =
    fact.image instanceof File
      ? URL.createObjectURL(fact.image)
      : typeof fact.image === "string"
        ? fact.image
        : null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        maxHeight: { xs: "85vh", sm: "90vh" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography
        fontWeight={700}
        fontSize={{ xs: 18, sm: 22, md: 24 }}
        fontFamily="Aeonik"
        sx={{
          background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
          color: "#fff",
          p: { xs: 1.5, sm: 2 },
          borderRadius: "8px 8px 0 0",
          flexShrink: 0,
        }}
      >
        {header}
      </Typography>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          backgroundColor: "#fff",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          flex: 1,
        }}
      >
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={labelSx}>Title *</FormLabel>
          <TextField
            value={fact.title}
            onChange={(e) => setFact({ ...fact, title: e.target.value })}
            placeholder="Enter title"
            error={!!errors.title}
            helperText={errors.title || `${fact.title.length}/${MAX_TITLE_LENGTH}`}
            inputProps={{ maxLength: MAX_TITLE_LENGTH }}
            sx={fieldSx}
          />
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={labelSx}>Image *</FormLabel>
          <Button
            component="label"
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: "8px", mb: 1 }}
          >
            {fact.image ? "Replace Image" : "Upload Image"}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleImageChange}
            />
          </Button>
          {errors.image && (
            <Typography color="error" variant="caption">
              {errors.image}
            </Typography>
          )}
          {previewUrl && (
            <Box sx={{ position: "relative", mt: 1, width: "fit-content" }}>
              <Box
                component="img"
                src={previewUrl}
                alt="preview"
                sx={{
                  width: 160,
                  height: 120,
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              />
              {!row?._id && (
                <RiCloseCircleFill
                  style={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    fontSize: 22,
                    cursor: "pointer",
                    color: "#d32f2f",
                    background: "#fff",
                    borderRadius: "50%",
                  }}
                  onClick={() => setFact({ ...fact, image: null })}
                />
              )}
            </Box>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={labelSx}>Content *</FormLabel>
          <TextField
            value={fact.content}
            onChange={(e) => setFact({ ...fact, content: e.target.value })}
            placeholder="Enter content"
            multiline
            minRows={5}
            error={!!errors.content}
            helperText={errors.content}
            sx={fieldSx}
          />
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel sx={labelSx}>Display Order *</FormLabel>
          <TextField
            type="number"
            value={fact.displayOrder}
            onChange={(e) => setFact({ ...fact, displayOrder: e.target.value })}
            inputProps={{ min: 0 }}
            error={!!errors.displayOrder}
            helperText={errors.displayOrder}
            sx={fieldSx}
          />
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={!!fact.isActive}
              onChange={(e) => setFact({ ...fact, isActive: e.target.checked })}
              color="primary"
            />
          }
          label={fact.isActive ? "Active" : "Inactive"}
          sx={{ mb: 2 }}
        />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => onCancel && onCancel()}
            disabled={submitting}
            sx={{ ...gradientBtnSx, background: "transparent", color: "#752ADA" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={gradientBtnSx}
          >
            {submitting ? "Saving..." : button_name}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
